import { useCallback, useEffect, useState } from "react";
import { Bot, CalendarClock, CheckCircle2, Circle, Globe, Link2, ListChecks, Loader2, MailCheck, Play, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STAGE_LABELS = {
  research: "Research & Keywords",
  brand: "Brand Voice & Links",
  competitors: "Competitor Scan",
  write: "Writing (SEO + AEO + GEO)",
  images: "AI Images",
  save: "Save to Journal",
};
const STAGE_ORDER = Object.keys(STAGE_LABELS);

const MODE_OPTIONS = [
  { value: "off", label: "Off — manual runs only" },
  { value: "draft", label: "Weekly auto-draft — you review before it goes live" },
  { value: "auto", label: "Weekly auto-publish — fully automatic" },
];

const StatusChip = ({ status }) => {
  const styles = {
    running: "bg-[#D4AF37]/15 text-[#8a6d1f]",
    done: "bg-green-100 text-green-800",
    error: "bg-red-100 text-red-700",
  };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] ${styles[status] || "bg-gray-100 text-gray-500"}`}>
      {status}
    </span>
  );
};

// SEO Content Agent — one topic in, a fully researched SEO+AEO+GEO article out.
export const SeoAgentCard = ({ onArticleSaved }) => {
  const [topic, setTopic] = useState("");
  const [publishNow, setPublishNow] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [config, setConfig] = useState({ mode: "off", frequency: "week", engine: "premium", framework_brain: "free", autopilot: false, has_gemini_key: false, has_nvidia_key: false, has_groq_key: false, queue: [], competitor_urls: [], last_run: null, outreach_auto: false, has_smtp_pass: false, outreach_last_run: null });
  const [geminiKey, setGeminiKey] = useState("");
  const [nvidiaKey, setNvidiaKey] = useState("");
  const [groqKey, setGroqKey] = useState("");
  const [generatingTopics, setGeneratingTopics] = useState(false);
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const [backlinks, setBacklinks] = useState([]);
  const [radarBusy, setRadarBusy] = useState(false);
  const [draftFor, setDraftFor] = useState("");
  const [draftText, setDraftText] = useState("");
  const [queueInput, setQueueInput] = useState("");
  const [competitorInput, setCompetitorInput] = useState("");
  const [editingKey, setEditingKey] = useState(null);
  const [starting, setStarting] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState("automation");
  const [smtpForm, setSmtpForm] = useState({ host: "", port: 587, user: "", pass: "", from_name: "" });
  const [smtpSynced, setSmtpSynced] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [outreachBusy, setOutreachBusy] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/seo-agent/jobs");
      setJobs(data);
      const running = data.find((j) => j.status === "running");
      if (running) setActiveJob((cur) => (cur?.status === "running" ? cur : running));
    } catch {
      /* quiet */
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/seo-agent/config");
      setConfig(data);
      setSmtpSynced((synced) => {
        if (!synced) {
          setSmtpForm({ host: data.outreach_smtp_host || "", port: data.outreach_smtp_port || 587, user: data.outreach_smtp_user || "", pass: "", from_name: data.outreach_from_name || "" });
        }
        return true;
      });
    } catch {
      /* quiet */
    }
  }, []);

  useEffect(() => {
    loadJobs();
    loadConfig();
    api.get("/admin/seo-agent/backlinks").then(({ data }) => setBacklinks(data.opportunities || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadJobs, loadConfig]);

  // poll the running job until it settles
  useEffect(() => {
    if (!activeJob || activeJob.status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/seo-agent/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (data.status !== "running") {
          loadJobs();
          if (data.status === "done") {
            toast.success(`Article ready: "${data.article_title}"${data.published ? " — published live" : " — saved as draft"}`);
            onArticleSaved?.();
          } else {
            toast.error(data.error || "The agent stumbled — please try again");
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.id, activeJob?.status]);

  const saveConfig = async (patch, note) => {
    try {
      const { data } = await api.put("/admin/seo-agent/config", patch);
      setConfig(data);
      if (note) toast.success(note);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    }
  };

  const runAgent = async () => {
    if (topic.trim().length < 3) {
      toast.error("Give the agent a topic first — e.g. 'hydrafacial price dubai'");
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post("/admin/seo-agent/run", { topic: topic.trim(), publish: publishNow });
      const { data: job } = await api.get(`/admin/seo-agent/jobs/${data.job_id}`);
      setActiveJob(job);
      setTopic("");
      toast.success("The SEO agent is on it — watch the stages below");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start the agent");
    } finally {
      setStarting(false);
    }
  };

  const addQueueTopic = () => {
    const parts = queueInput.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!parts.length) return;
    const merged = [...config.queue];
    for (const t of parts) if (!merged.includes(t) && merged.length < 50) merged.push(t);
    saveConfig({ queue: merged }, parts.length > 1 ? `${parts.length} topics queued` : "Topic queued");
    setQueueInput("");
  };

  const addCompetitor = () => {
    const parts = competitorInput.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
    const valid = parts.filter((u) => u.startsWith("http"));
    if (!valid.length) {
      toast.error("Paste full URLs starting with https://");
      return;
    }
    const merged = [...(config.competitor_urls || [])];
    for (const u of valid) if (!merged.includes(u) && merged.length < 100) merged.push(u);
    saveConfig({ competitor_urls: merged }, `${valid.length} competitor${valid.length === 1 ? "" : "s"} added`);
    setCompetitorInput("");
  };

  const isFramework = config.engine === "crewai" || config.engine === "langgraph" || config.engine === "hybrid";
  const freeBrain = config.engine === "free" || (isFramework && config.framework_brain !== "premium");

  return (
    <div className="glass rounded-2xl p-6" data-testid="seo-agent">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#1A1615] to-[#4A4A4A]">
          <Bot className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-serif text-xl font-semibold text-[#1A1615]">SEO Content Agent</h2>
          <p className="text-xs font-light text-[#91837C]">
            Your auto SEO team: researches keywords, reads competitor sites, writes for Google + ChatGPT + Gemini + Perplexity, paints AI images, and files the article in your Journal.
          </p>
        </div>
      </div>

      {/* run */}
      <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto]">
        <input
          className="input-lux text-sm"
          placeholder="Topic or keyword, e.g. profhilo vs fillers dubai"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !starting && runAgent()}
          data-testid="seo-agent-topic"
          disabled={starting || activeJob?.status === "running"}
        />
        <button onClick={runAgent} disabled={starting || activeJob?.status === "running"} data-testid="seo-agent-run" className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 text-xs disabled:opacity-60">
          {starting || activeJob?.status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {activeJob?.status === "running" ? "Agent working…" : "Run the Agent"}
        </button>
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs font-light text-[#5A504B]">
        <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} data-testid="seo-agent-publish-now" className="accent-[#B38B2D]" />
        Publish immediately when finished (default: save as a draft for your review)
      </label>

      {/* live progress */}
      {activeJob && (
        <div className="mt-5 rounded-xl bg-white/70 p-5" data-testid="seo-agent-progress">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold text-[#1A1615]">
              {activeJob.status === "running" ? "Working on: " : "Last run: "}
              <span className="font-light">{activeJob.topic}</span>
            </p>
            <StatusChip status={activeJob.status} />
          </div>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {STAGE_ORDER.map((st) => {
              const s = activeJob.stages?.[st] || { status: "pending" };
              return (
                <li key={st} className="flex items-start gap-2 text-xs" data-testid={`seo-agent-stage-${st}`}>
                  {s.status === "done" ? (
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />
                  ) : s.status === "running" ? (
                    <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-[#B38B2D]" />
                  ) : (
                    <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#D9CFC6]" />
                  )}
                  <span>
                    <span className="font-medium text-[#1A1615]">{STAGE_LABELS[st]}</span>
                    {s.detail && <span className="block font-light text-[#91837C]">{s.detail}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
          {activeJob.status === "running" && (
            <p className="mt-3 text-[10px] font-light text-[#91837C]">
              {isFramework
                ? "Framework engine runs take about 4–10 minutes — specialist agents are collaborating in sequence."
                : config.engine === "free"
                ? "Free engine runs take about 3–8 minutes — six specialists are working in sequence, at $0 cost."
                : "A full run takes about 2–4 minutes — six specialists are working in sequence."}
            </p>
          )}
        </div>
      )}

      {/* engine */}
      <div className="mt-6 rounded-xl bg-white/70 p-4" data-testid="seo-agent-engine">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">AI Engine</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {[
            { id: "premium", name: "Premium AI", desc: "Best quality, ~10–15¢/article", note: "Premium engine selected" },
            { id: "free", name: "Free Tools — $0", desc: "Single-agent free cascade, no key needed", note: "Free engine + Autopilot on — fully hands-free at $0" },
            { id: "crewai", name: "CrewAI Department", desc: "Open-source · strategist → writer → editor agents", note: "CrewAI engine selected — a role-based editorial department" },
            { id: "langgraph", name: "LangGraph Flow", desc: "Open-source · draft → critique → revise quality loop", note: "LangGraph engine selected — drafts are critiqued and revised" },
            { id: "hybrid", name: "Hybrid Dream Team", desc: "Best of all: CrewAI department + LangGraph review loop on the free cascade", note: "Hybrid engine selected — team-written, then quality-loop reviewed" },
          ].map((e) => (
            <button
              key={e.id}
              onClick={() => saveConfig(e.id === "free" ? { engine: "free", autopilot: true } : { engine: e.id }, e.note)}
              data-testid={`seo-agent-engine-${e.id}`}
              className={`rounded-xl border p-3 text-left transition-all duration-200 ${
                config.engine === e.id
                  ? "border-[#D4AF37] bg-[#F8F3E6] shadow-[0_6px_18px_rgba(212,175,55,0.12)]"
                  : "border-[#EBE3DA] bg-white hover:border-[#D4AF37]/50"
              }`}
            >
              <span className={`block text-xs font-semibold ${config.engine === e.id ? "text-[#1A1615]" : "text-[#5A504B]"}`}>{e.name}</span>
              <span className="mt-0.5 block text-[10px] font-light text-[#91837C]">{e.desc}</span>
            </button>
          ))}
        </div>
        {isFramework && (
          <div className="mt-3 rounded-lg border border-[#EBE3DA] bg-white/60 p-3" data-testid="seo-agent-framework-brain">
            <p className="text-[11px] font-medium text-[#5A504B]">
              Framework brain — which AI the agents think with. The framework itself is free, open-source and optional.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                onClick={() => saveConfig({ framework_brain: "free" }, "Framework brain: free cascade ($0)")}
                data-testid="seo-agent-brain-free"
                className={`rounded-full px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${config.framework_brain !== "premium" ? "bg-[#1A1615] text-[#D4AF37]" : "bg-white text-[#91837C] hover:text-[#1A1615]"}`}
              >
                Free cascade — $0
              </button>
              <button
                onClick={() => saveConfig({ framework_brain: "premium" }, "Framework brain: premium AI")}
                data-testid="seo-agent-brain-premium"
                className={`rounded-full px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${config.framework_brain === "premium" ? "bg-[#1A1615] text-[#D4AF37]" : "bg-white text-[#91837C] hover:text-[#1A1615]"}`}
              >
                Premium AI — sharper drafts
              </button>
            </div>
          </div>
        )}
        {freeBrain && (
          <div className="mt-3 space-y-3">
            <p className="text-[11px] font-light leading-relaxed text-[#5A504B]">
              Research &amp; writing run on free AI — Pollinations by default (no key needed), upgraded automatically if you add free-tier keys below.
              Images are painted by Pollinations Flux with our engineered brand prompts. Slower than premium, but every run costs <span className="font-semibold text-[#1A1615]">$0</span> — every-minute cadence is safe.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                { id: "nvidia", label: "NVIDIA", hint: "build.nvidia.com/settings", saved: config.has_nvidia_key, value: nvidiaKey, setValue: setNvidiaKey, field: "nvidia_key" },
                { id: "gemini", label: "Gemini", hint: "aistudio.google.com", saved: config.has_gemini_key, value: geminiKey, setValue: setGeminiKey, field: "gemini_key" },
                { id: "groq", label: "Groq", hint: "console.groq.com", saved: config.has_groq_key, value: groqKey, setValue: setGroqKey, field: "groq_key" },
              ].map((k) =>
                k.saved && editingKey !== k.id ? (
                  <div key={k.id} className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3.5 py-2.5" data-testid={`seo-agent-${k.id}-status`}>
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                    <span className="min-w-0 flex-1 text-xs font-medium text-[#1A1615]">{k.label} key — connected</span>
                    <button
                      onClick={() => setEditingKey(k.id)}
                      data-testid={`seo-agent-${k.id}-replace`}
                      className="text-[10px] font-semibold text-[#B38B2D] underline underline-offset-2"
                    >
                      Replace
                    </button>
                    <button
                      onClick={() => saveConfig({ [k.field]: "" }, `${k.label} key removed`)}
                      data-testid={`seo-agent-${k.id}-clear`}
                      className="text-[10px] font-semibold text-[#91837C] transition-colors hover:text-red-500"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div key={k.id} className="flex gap-2">
                    <input
                      type="password"
                      className="input-lux flex-1 text-xs"
                      placeholder={`Optional: free ${k.label} key (${k.hint})`}
                      value={k.value}
                      onChange={(e) => k.setValue(e.target.value)}
                      data-testid={`seo-agent-${k.id}-key`}
                    />
                    <button
                      onClick={() => {
                        if (!k.value.trim()) return;
                        saveConfig({ [k.field]: k.value }, `${k.label} key saved`);
                        k.setValue("");
                        setEditingKey(null);
                      }}
                      data-testid={`seo-agent-${k.id}-save`}
                      className="btn-outline-lux px-3 py-2 text-[10px]"
                    >
                      Save
                    </button>
                    {k.saved && (
                      <button onClick={() => { k.setValue(""); setEditingKey(null); }} className="text-[10px] font-semibold text-[#91837C]" data-testid={`seo-agent-${k.id}-cancel`}>
                        Cancel
                      </button>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {/* workspace — one full-width panel at a time instead of four cramped columns */}
      <div className="mt-6" data-testid="seo-agent-workspace">
        <div className="flex flex-wrap gap-2">
          {[
            { id: "automation", label: "Automation", icon: CalendarClock },
            { id: "queue", label: `Topic Queue (${config.queue.length})`, icon: ListChecks },
            { id: "competitors", label: `Competitors (${(config.competitor_urls || []).length})`, icon: Globe },
            { id: "backlinks", label: `Backlink Radar (${backlinks.length})`, icon: Link2 },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setWorkspaceTab(t.id)}
              data-testid={`seo-agent-subtab-${t.id}`}
              aria-current={workspaceTab === t.id ? "page" : undefined}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors duration-200 ${
                workspaceTab === t.id ? "bg-[#1A1615] text-[#D4AF37]" : "bg-white text-[#91837C] hover:text-[#1A1615]"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" aria-hidden="true" /> {t.label}
            </button>
          ))}
        </div>
        <div className="mt-4">
        {workspaceTab === "automation" && (
        <div className="rounded-xl bg-white/70 p-5 sm:p-6">
          <div className="grid gap-6 sm:grid-cols-2">
          <div>
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">
            <CalendarClock className="h-3.5 w-3.5" /> Automation
          </p>
          <select
            value={config.mode}
            onChange={(e) => saveConfig({ mode: e.target.value }, "Automation mode saved")}
            data-testid="seo-agent-mode"
            className="input-lux mt-3 w-full text-xs"
          >
            {MODE_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <label className="mt-3 block text-[10px] font-semibold uppercase tracking-[0.2em] text-[#91837C]">Run every</label>
          <select
            value={config.frequency || "week"}
            onChange={(e) => saveConfig({ frequency: e.target.value }, "Frequency saved")}
            data-testid="seo-agent-frequency"
            className="input-lux mt-1.5 w-full text-xs"
          >
            <option value="minute">Minute — rapid-fire while topics remain</option>
            <option value="hour">Hour</option>
            <option value="day">Day</option>
            <option value="week">Week (recommended)</option>
          </select>
          {(config.frequency === "minute" || config.frequency === "hour") && config.mode !== "off" && !freeBrain && (
            <p className="mt-2 rounded-lg bg-[#FAF3E3] px-3 py-2 text-[10px] font-medium text-[#8a6d1f]" data-testid="seo-agent-frequency-warning">
              Every run spends AI credits. The agent stops the moment your topic queue is empty — the queue is your budget control.
            </p>
          )}
          {(config.frequency === "minute" || config.frequency === "hour") && config.mode !== "off" && freeBrain && (
            <p className="mt-2 rounded-lg bg-green-50 px-3 py-2 text-[10px] font-medium text-green-800" data-testid="seo-agent-frequency-note">
              Free engine: these runs cost $0 — cadence is limited only by speed (about 3–8 minutes per article).
            </p>
          )}
          {config.autopilot && config.mode !== "off" && !freeBrain && (
            <p className="mt-2 rounded-lg bg-[#FAF3E3] px-3 py-2 text-[10px] font-medium text-[#8a6d1f]" data-testid="seo-agent-autopilot-warning">
              Autopilot with the Premium engine spends credits automatically — switch to Free Tools for $0 hands-free runs.
            </p>
          )}
          </div>
          <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Autopilot</p>
          <label className="mt-3 flex items-start gap-2 text-xs font-light text-[#5A504B]">
            <input
              type="checkbox"
              checked={!!config.autopilot}
              onChange={(e) => saveConfig({ autopilot: e.target.checked }, e.target.checked ? "Autopilot on — it picks its own keywords" : "Autopilot off")}
              data-testid="seo-agent-autopilot"
              className="mt-0.5 accent-[#B38B2D]"
            />
            <span>
              <span className="font-medium text-[#1A1615]">Autopilot keyword research</span> — when the queue runs dry, the agent studies your treatments and past articles, researches fresh Dubai keywords, and queues them itself.
            </span>
          </label>
          {config.last_run && (
            <p className="mt-2 text-[10px] font-light text-[#91837C]" data-testid="seo-agent-last-run">
              Last automatic run: {new Date(config.last_run).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
          </div>
          </div>
        </div>
        )}

        {workspaceTab === "queue" && (
        <div className="rounded-xl bg-white/70 p-5 sm:p-6">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">
            <ListChecks className="h-3.5 w-3.5" /> Topic Queue ({config.queue.length})
            <button
              onClick={async () => {
                setGeneratingTopics(true);
                try {
                  const { data } = await api.post("/admin/seo-agent/autopilot-topics", {}, { timeout: 120000 });
                  setConfig((c) => ({ ...c, queue: data.queue }));
                  toast.success(data.added ? `Autopilot added ${data.added} fresh topics` : "No new topics — everything's covered");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Topic research failed — try again");
                } finally {
                  setGeneratingTopics(false);
                }
              }}
              disabled={generatingTopics}
              data-testid="seo-agent-generate-topics"
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#1A1615] px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#D4AF37] transition-opacity disabled:opacity-60"
            >
              {generatingTopics ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Auto-pick
            </button>
          </p>
          <div className="mt-3 flex gap-2">
            <input
              className="input-lux flex-1 text-xs"
              placeholder="Add a topic for the weekly run"
              value={queueInput}
              onChange={(e) => setQueueInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addQueueTopic()}
              data-testid="seo-agent-queue-input"
            />
            <button onClick={addQueueTopic} data-testid="seo-agent-queue-add" className="btn-gold px-3 py-2 text-[10px]" aria-label="Add topic">
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="mt-3 grid max-h-96 gap-1.5 overflow-y-auto sm:grid-cols-2">
            {config.queue.map((t, i) => (
              <li key={t} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-1.5 text-xs text-[#5A504B]" data-testid={`seo-agent-queue-item-${i}`}>
                <span className="truncate">{t}</span>
                <button
                  onClick={() => saveConfig({ queue: config.queue.filter((x) => x !== t) }, "Topic removed")}
                  data-testid={`seo-agent-queue-remove-${i}`}
                  className="text-[#C9B8A8] transition-colors hover:text-red-500"
                  aria-label={`Remove ${t}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </li>
            ))}
            {!config.queue.length && <li className="text-[10px] font-light text-[#91837C]">Queue topics and the agent drafts them automatically at your chosen rhythm.</li>}
            {(config.dead_topics || []).length > 0 && (
              <li className="text-[10px] font-light text-[#C9B8A8]" data-testid="seo-agent-dead-topics">
                Dropped after repeated provider failures: {(config.dead_topics || []).slice(-3).join(" · ")}
              </li>
            )}
          </ul>
        </div>
        )}

        {workspaceTab === "competitors" && (
        <div className="rounded-xl bg-white/70 p-5 sm:p-6">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">
            <Globe className="h-3.5 w-3.5" /> Competitors ({(config.competitor_urls || []).length}/100)
            <button
              onClick={async () => {
                setDiscoverBusy(true);
                try {
                  const { data } = await api.post("/admin/seo-agent/discover-competitors", {}, { timeout: 180000 });
                  setConfig((c) => ({ ...c, competitor_urls: [...(c.competitor_urls || []), ...data.new_urls] }));
                  toast.success(data.added ? `Discovered ${data.added} real competitor sites (free search)` : "No new competitors found — your list is comprehensive");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Discovery failed — try again");
                } finally {
                  setDiscoverBusy(false);
                }
              }}
              disabled={discoverBusy}
              data-testid="seo-agent-discover"
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#1A1615] px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#D4AF37] transition-opacity disabled:opacity-60"
            >
              {discoverBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Auto-find 50
            </button>
          </p>
          <div className="mt-3 flex gap-2">
            <input
              className="input-lux flex-1 text-xs"
              placeholder="Paste URLs — one or many, comma-separated"
              value={competitorInput}
              onChange={(e) => setCompetitorInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCompetitor()}
              data-testid="seo-agent-competitor-input"
            />
            <button onClick={addCompetitor} data-testid="seo-agent-competitor-add" className="btn-gold px-3 py-2 text-[10px]" aria-label="Add competitor">
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="mt-3 grid max-h-96 gap-1.5 overflow-y-auto sm:grid-cols-2">
            {(config.competitor_urls || []).map((u, i) => (
              <li key={u} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-1.5 text-xs text-[#5A504B]" data-testid={`seo-agent-competitor-item-${i}`}>
                <span className="truncate">{u.replace(/^https?:\/\//, "")}</span>
                <button
                  onClick={() => saveConfig({ competitor_urls: config.competitor_urls.filter((x) => x !== u) }, "Competitor removed")}
                  data-testid={`seo-agent-competitor-remove-${i}`}
                  className="text-[#C9B8A8] transition-colors hover:text-red-500"
                  aria-label={`Remove ${u}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </li>
            ))}
            {!(config.competitor_urls || []).length && <li className="text-[10px] font-light text-[#91837C]">Add rival clinic blogs — the agent finds the topics they miss.</li>}
          </ul>
        </div>
        )}

        {workspaceTab === "backlinks" && (
        <div className="rounded-xl bg-white/70 p-5 sm:p-6">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">
            <Link2 className="h-3.5 w-3.5" /> Backlink Radar ({backlinks.length})
            <button
              onClick={async () => {
                setRadarBusy(true);
                try {
                  const { data } = await api.post("/admin/seo-agent/backlink-radar", {}, { timeout: 180000 });
                  setBacklinks(data.opportunities || []);
                  toast.success(data.added ? `Found ${data.added} new backlink opportunities` : "Radar refreshed — list is current");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Radar failed — try again");
                } finally {
                  setRadarBusy(false);
                }
              }}
              disabled={radarBusy}
              data-testid="seo-agent-backlink-run"
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#1A1615] px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#D4AF37] transition-opacity disabled:opacity-60"
            >
              {radarBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Scan
            </button>
          </p>
          <p className="mt-2 text-[10px] font-light text-[#91837C]">Real authority = real placements. This finds free directories &amp; guest-post spots and drafts your outreach email.</p>

          {/* free auto email outreach — company SMTP + AI review gate */}
          <div className="mt-4 rounded-xl border border-[#EBE3DA] bg-[#FAF8F5] p-4" data-testid="outreach-auto">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Auto Email Outreach — free via your company email</p>
            <p className="mt-1 text-[10px] font-light leading-relaxed text-[#91837C]">
              Connect your company email once (Gmail/Google Workspace: use an <b>App Password</b> — myaccount.google.com/apppasswords; Outlook/Zoho: SMTP password). The agent finds each site's contact email, drafts the message, an AI editor reviews it (weak drafts are never sent), and it emails automatically — max a few per day to protect your sender reputation.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <input className="input-lux text-xs" placeholder="SMTP host, e.g. smtp.gmail.com" value={smtpForm.host} onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })} data-testid="outreach-smtp-host" />
              <input className="input-lux text-xs" placeholder="Port (587 or 465)" value={smtpForm.port} onChange={(e) => setSmtpForm({ ...smtpForm, port: e.target.value })} data-testid="outreach-smtp-port" />
              <input className="input-lux text-xs" placeholder="From name, e.g. Royal Skin Clinic" value={smtpForm.from_name} onChange={(e) => setSmtpForm({ ...smtpForm, from_name: e.target.value })} data-testid="outreach-from-name" />
              <input className="input-lux text-xs" placeholder="Your company email (username)" value={smtpForm.user} onChange={(e) => setSmtpForm({ ...smtpForm, user: e.target.value })} data-testid="outreach-smtp-user" />
              <input type="password" className="input-lux text-xs" placeholder={config.has_smtp_pass ? "App password saved — paste to replace" : "Email app password"} value={smtpForm.pass} onChange={(e) => setSmtpForm({ ...smtpForm, pass: e.target.value })} data-testid="outreach-smtp-pass" />
              <button
                onClick={() => saveConfig({ smtp_host: smtpForm.host, smtp_port: smtpForm.port, smtp_user: smtpForm.user, from_name: smtpForm.from_name, ...(smtpForm.pass ? { smtp_pass: smtpForm.pass } : {}) }, "Email connection saved")}
                data-testid="outreach-save"
                className="btn-gold px-4 py-2 text-[10px]"
              >
                Save Email Connection
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input className="input-lux flex-1 text-xs sm:max-w-56" placeholder="Your email for a test" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="outreach-test-to" />
              <button
                onClick={async () => {
                  try {
                    await api.post("/admin/seo-agent/outreach-test", { to: testTo.trim() }, { timeout: 60000 });
                    toast.success(`Test email sent to ${testTo.trim()} — check your inbox`);
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test failed — check host, port and app password");
                  }
                }}
                data-testid="outreach-test-send"
                className="btn-outline-lux px-4 py-2 text-[10px]"
              >
                Send Test
              </button>
              <label className="flex items-center gap-2 text-xs font-light text-[#5A504B]">
                <input
                  type="checkbox"
                  checked={!!config.outreach_auto}
                  onChange={(e) => saveConfig({ outreach_auto: e.target.checked }, e.target.checked ? "Auto outreach on — emails go out daily, AI-reviewed" : "Auto outreach paused")}
                  data-testid="outreach-auto-toggle"
                  className="accent-[#B38B2D]"
                />
                <span><span className="font-medium text-[#1A1615]">Fully automatic</span> — email new opportunities daily</span>
              </label>
              <button
                onClick={async () => {
                  setOutreachBusy(true);
                  try {
                    const { data } = await api.post("/admin/seo-agent/outreach-run", { limit: 5 }, { timeout: 240000 });
                    toast.success(data.sent ? `Sent ${data.sent} AI-reviewed outreach email${data.sent === 1 ? "" : "s"}` : `Nothing sent — ${data.attempted ? "no contactable opportunities right now" : "no fresh opportunities"}`);
                    const { data: bl } = await api.get("/admin/seo-agent/backlinks");
                    setBacklinks(bl.opportunities || []);
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Outreach run failed");
                  } finally {
                    setOutreachBusy(false);
                  }
                }}
                disabled={outreachBusy}
                data-testid="outreach-run-now"
                className="btn-outline-lux inline-flex items-center gap-1.5 px-4 py-2 text-[10px] disabled:opacity-60"
              >
                {outreachBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Run Outreach Now
              </button>
              {config.outreach_last_run && (
                <span className="text-[10px] font-light text-[#91837C]" data-testid="outreach-last-run">
                  Last run: {new Date(config.outreach_last_run).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>
          </div>

          <ul className="mt-3 max-h-[26rem] space-y-1.5 overflow-y-auto" data-testid="backlink-list">
            {backlinks.map((b, i) => (
              <li key={b.url} className="rounded-lg bg-white px-3 py-2" data-testid={`backlink-item-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-[#1A1615]" title={b.url}>{b.name}</span>
                  {b.emailed_at && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.08em] text-green-800" title={`Emailed ${b.email_to || ""}`} data-testid={`backlink-emailed-${i}`}>
                      <MailCheck className="h-2.5 w-2.5" /> emailed
                    </span>
                  )}
                  <span className="rounded-full bg-[#F5EBE6] px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.08em] text-[#8a6d1f]">{String(b.type).replace(/_/g, " ")}</span>
                  <select
                    value={b.status || "new"}
                    onChange={async (e) => {
                      await api.post("/admin/seo-agent/backlinks/status", { url: b.url, status: e.target.value });
                      setBacklinks((bs) => bs.map((x) => (x.url === b.url ? { ...x, status: e.target.value } : x)));
                    }}
                    data-testid={`backlink-status-${i}`}
                    className="rounded border border-[#E8E2D9] bg-white px-1 py-0.5 text-[9px] text-[#5A504B]"
                  >
                    <option value="new">new</option>
                    <option value="contacted">contacted</option>
                    <option value="won">won</option>
                    <option value="skipped">skipped</option>
                  </select>
                  <button
                    onClick={async () => {
                      setDraftFor(b.url);
                      setDraftText("Drafting…");
                      try {
                        const { data } = await api.post("/admin/seo-agent/outreach-draft", { url: b.url, name: b.name }, { timeout: 120000 });
                        setDraftText(data.draft);
                      } catch {
                        setDraftText("Draft failed — try again");
                      }
                    }}
                    data-testid={`backlink-draft-${i}`}
                    className="text-[9px] font-semibold text-[#B38B2D] underline underline-offset-2"
                  >
                    Draft email
                  </button>
                </div>
                {b.why && <p className="mt-1 truncate text-[9px] font-light text-[#91837C]">{b.why}</p>}
              </li>
            ))}
            {!backlinks.length && <li className="text-[10px] font-light text-[#91837C]">Scan to find free directory listings and guest-post spots.</li>}
          </ul>
        </div>
        )}
        </div>
      </div>

      {/* recent jobs */}
      {jobs.length > 0 && (
        <div className="mt-6" data-testid="seo-agent-jobs">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#91837C]">Recent Runs</p>
            {jobs.some((j) => j.status === "error") && (
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.delete("/admin/seo-agent/jobs");
                    toast.success(data.deleted ? `Cleared ${data.deleted} failed run${data.deleted === 1 ? "" : "s"}` : "Nothing to clear");
                    loadJobs();
                  } catch {
                    toast.error("Could not clear failed runs");
                  }
                }}
                data-testid="seo-agent-clear-failed"
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#91837C] transition-colors hover:text-red-500"
              >
                <Trash2 className="h-3 w-3" /> Clear failed
              </button>
            )}
          </div>
          <ul className="mt-3 space-y-2">
            {jobs.map((j) => (
              <li key={j.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-white/70 px-4 py-2.5 text-xs" data-testid={`seo-agent-job-${j.id}`}>
                <StatusChip status={j.status} />
                <span className="min-w-0 flex-1 truncate font-medium text-[#1A1615]">{j.article_title || j.topic}</span>
                <span className="rounded-full bg-[#F5EBE6] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#8a6d1f]">{j.mode}</span>
                {j.status === "done" && (
                  <span className="text-[10px] font-light text-[#91837C]">{j.published ? "published" : "draft"}</span>
                )}
                <span className="text-[10px] font-light text-[#91837C]">
                  {new Date(j.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* outreach email draft — popup dialog, no in-list scrolling */}
      {draftFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="backlink-draft-modal" role="dialog" aria-modal="true" aria-label="Outreach email draft">
          <div className="absolute inset-0 bg-[#1A1615]/40 backdrop-blur-sm" onClick={() => setDraftFor("")} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Outreach email draft</p>
            <p className="mt-1 truncate text-xs font-light text-[#91837C]">{draftFor}</p>
            <textarea readOnly rows={10} value={draftText} className="input-lux mt-3 w-full resize-none text-xs" data-testid="backlink-draft-text" />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDraftFor("")}
                data-testid="backlink-draft-close"
                className="btn-outline-lux px-5 py-2.5 text-xs"
              >
                Close
              </button>
              <button
                onClick={() => { navigator.clipboard.writeText(draftText); toast.success("Copied — paste into your email"); }}
                data-testid="backlink-copy"
                className="btn-gold px-5 py-2.5 text-xs"
              >
                Copy email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
